import * as React from 'react';
import { cn } from '@/lib/cn';

// UI_BUILD_SPEC §2.16. Opens after a 400ms hover delay but immediately on
// keyboard focus — a keyboard user has no way to "hover and wait", so delaying
// focus would make icon-only controls undiscoverable.
const HOVER_DELAY_MS = 400;

interface TooltipProps {
  label: string;
  children: React.ReactElement;
  side?: 'top' | 'bottom';
  className?: string;
}

export function Tooltip({ label, children, side = 'top', className }: TooltipProps) {
  const [open, setOpen] = React.useState(false);
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>();
  const id = React.useId();

  React.useEffect(() => () => clearTimeout(timerRef.current), []);

  const show = React.useCallback((immediate: boolean) => {
    clearTimeout(timerRef.current);
    if (immediate) { setOpen(true); return; }
    timerRef.current = setTimeout(() => setOpen(true), HOVER_DELAY_MS);
  }, []);

  const hide = React.useCallback(() => {
    clearTimeout(timerRef.current);
    setOpen(false);
  }, []);

  React.useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') hide();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, hide]);

  return (
    <span className={cn('relative inline-flex', className)}>
      {React.cloneElement(children, {
        'aria-describedby': open ? id : undefined,
        onMouseEnter: () => show(false),
        onMouseLeave: hide,
        onFocus: () => show(true),
        onBlur: hide,
      })}
      {open && (
        <span
          id={id}
          role="tooltip"
          className={cn(
            'pointer-events-none absolute left-1/2 z-dropdown w-max max-w-60 -translate-x-1/2 rounded-sm bg-ink px-2 py-1.5 text-xs text-white shadow-raised',
            side === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
          )}
        >
          {label}
        </span>
      )}
    </span>
  );
}
